import { SiteUrls } from '@tableau/shared-urls';

import { RefreshContentScope } from '../../app/ActionPlaceScopes';
import { appStore } from '../../app/AppStore';
import {
  addContentTypeToProjectHierarchy,
  populateContentBreadcrumbs,
  watchUrlForBreadcrumbUpdates
} from '../../app/content/breadcrumbs/BreadcrumbHelpers';
import { IPlaceScope } from '../../app/IPlaceScope';
import { siteUrlsSelector } from '../../app/selectors/SiteUrlsSelectors';
import { addUrlWatchersToScopeForExplorer } from '../../app/spaces/Explorer/ExplorerUrlBindings';
import { getQueryStringServiceFromAngular } from '../../common/react/QueryString';
import { FlowSpaceOwnProps } from '../../flow/components/FlowSpace';
import { FlowSubplaceProps } from '../../flow/types/FlowSubplaceProps';
import { AngularContextFactory } from '../factories/AngularContextFactory';
import { IActionsMenuScope } from '../helpers/ActionButtonsHelper';
import { ISubplaceTabsScope } from '../helpers/SubplaceCountsHelper';
import { isFlowPlacePath } from '../helpers/UrlHelpers';
import { FlowDetailInfo, FlowDetailWithUsageInfo, FlowListItemInfo } from '../models/FlowInfo';
import { IPlace } from '../models/IPlace';
import { INamedItemInfo } from '../models/resourceInfoParts';
import { Module } from '../Module';
import { FlowAllowedActions } from '../ServerApi/types/FlowAllowedActions';
import { FavoriteService } from '../services/FavoriteService';
import { FlowActions } from '../services/FlowActions';
import { ServerService } from '../services/ServerService';
import { Telemetry } from '../services/TelemetryService';
import { ListActionsScope } from './ContentSubplaceCtrl';
import { FlowPublishResultService } from './FlowPublishResultCtrl';

export type FlowPlaceScope =
  IPlaceScope &
  ISubplaceTabsScope &
  IActionsMenuScope<FlowListItemInfo, {}> &
  ListActionsScope &
  RefreshContentScope &
  {
    flow: FlowDetailWithUsageInfo;
    subplaceProps: FlowSubplaceProps;
    flowSpaceOwnProps: FlowSpaceOwnProps;
    hideSearch: boolean;
  };

export class FlowPlaceCtrl {
  private currentPath: string;

  public static $inject = [
    '$filter',
    '$location',
    '$sanitize',
    '$scope',
    '$state',
    '$translate',
    'AngularContextFactory',
    'FavoriteService',
    'FlowActions',
    'flowAllowedActions',
    'flowDetailInfo',
    'flowListItemInfo',
    'FlowPublishResultService',
    'place',
    'projectHierarchy',
    'ServerService',
    'TelemetryService',
  ];

  constructor(
    $filter: ng.IFilterService,
    private $location: ng.ILocationService,
    $sanitize: ng.sanitize.ISanitizeService,
    private $scope: FlowPlaceScope,
    private $state: ng.ui.IStateService,
    private $translate: ng.translate.ITranslateService,
    angularContextFactory: AngularContextFactory,
    private FavoriteService: FavoriteService,
    private flowActions: FlowActions,
    private flowAllowedActions: FlowAllowedActions,
    private flowDetailInfo: FlowDetailInfo,
    private flowListItemInfo: FlowListItemInfo,
    flowPublishResultService: FlowPublishResultService,
    place: IPlace,
    private projectHierarchy: INamedItemInfo[],
    private ServerService: ServerService,
    telemetry: Telemetry,
  ) {
    const angularContext = angularContextFactory.create($scope);
    const flowDetailsWithUsage = {
      usageInfo: flowListItemInfo.usageInfo,
      usageInfoMaxValues: flowListItemInfo.usageInfoMaxValues,
      ...flowDetailInfo
    };
    const siteUrls = new SiteUrls({
      siteUrlName: this.ServerService.sessionSiteUrlName(),
      queryString: window.location.search,
    });

    // Currently only needed for Run History
    $scope.hideSearch = true;

    this.initBreadcrumbsOnScope();

    $scope.refreshContent =  () => $state.hardReload();
    this.currentPath = $location.path();

    $scope.flowSpaceOwnProps = {
      angularContext: angularContext,
      flowDetailInfo: flowDetailsWithUsage,
      flowAllowedActions: this.flowAllowedActions,
      flowListItemInfo: this.flowListItemInfo,
      place: place,
      sanitizer: $sanitize,
      scheduleFrequencyDescriptionFilter: $filter('scheduleFrequencyDescription'),
      siteUrls: siteUrls,
      subplaceName: $state.current.data.subplaceName,
      telemetry: telemetry,
      translate: $translate.instant.bind($translate),
      projectHierarchy: projectHierarchy
    };

    // add url watchers except for filters
    const queryString = getQueryStringServiceFromAngular($location);
    const getPath = () => {
      return $location.path();
    };

    addUrlWatchersToScopeForExplorer({ $scope, $location, queryString, store: appStore, getPreviousPath: getPath });

    const checkForPublishResult = (subplaceName: string) => {
      if (subplaceName === 'publishresult') {
        flowPublishResultService.handlePublishedFlow(flowDetailInfo);
        return 'overview';
      } else {
        return subplaceName;
      }
    };
    $scope.$watch(() => $state.current.data.subplaceName, () => {
      const subplaceName = checkForPublishResult($state.current.data.subplaceName);
      $scope.flowSpaceOwnProps = {
        ...$scope.flowSpaceOwnProps,
        subplaceName: subplaceName,
      };
    });
  }

  private initBreadcrumbsOnScope() {
    const projectHierarchyWithType = addContentTypeToProjectHierarchy(this.projectHierarchy);
    const siteUrls = siteUrlsSelector(appStore.getState());
    populateContentBreadcrumbs([ ...projectHierarchyWithType, this.flowListItemInfo ], siteUrls);
    watchUrlForBreadcrumbUpdates(this.$scope, this.$location,
      () => isFlowPlacePath(this.flowListItemInfo.id, this.$location.path()));
  }
}

Module.instance().controller('FlowPlaceCtrl', FlowPlaceCtrl);
